import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Music2, VolumeX, Volume1, Volume2, Waves, Wind, CloudRain, Trees, Flame, Brain, AudioLines } from "lucide-react";
import { startAmbient, stopAmbient, NATURE_KEYS, BINAURAL_KEYS, NOISE_KEYS, setVolume, getVolume } from "@/lib/ambient";

const LABELS = {
  fr: { title: "Ambiance sonore", hint: "Sons générés en direct", silence: "Silence", nature: "Nature", binaural: "Binaural", noise: "Bruits", volume: "Volume", headphones: "🎧 casque conseillé",
        vagues: "Vagues", vent: "Vent", pluie: "Pluie", foret: "Forêt", feu: "Feu de camp",
        delta: "Delta · sommeil", theta: "Thêta · méditation", alpha: "Alpha · calme", beta: "Bêta · focus", rose: "Bruit rose", brun: "Bruit brun" },
  en: { title: "Sound ambiance", hint: "Live-generated sounds", silence: "Silence", nature: "Nature", binaural: "Binaural", noise: "Noise", volume: "Volume", headphones: "🎧 headphones advised",
        vagues: "Waves", vent: "Wind", pluie: "Rain", foret: "Forest", feu: "Campfire",
        delta: "Delta · sleep", theta: "Theta · meditation", alpha: "Alpha · calm", beta: "Beta · focus", rose: "Pink noise", brun: "Brown noise" },
  es: { title: "Ambiente sonoro", hint: "Sonidos generados en vivo", silence: "Silencio", nature: "Naturaleza", binaural: "Binaural", noise: "Ruidos", volume: "Volumen", headphones: "🎧 auriculares aconsejados",
        vagues: "Olas", vent: "Viento", pluie: "Lluvia", foret: "Bosque", feu: "Fogata",
        delta: "Delta · sueño", theta: "Theta · meditación", alpha: "Alfa · calma", beta: "Beta · foco", rose: "Ruido rosa", brun: "Ruido marrón" },
  nl: { title: "Geluidssfeer", hint: "Live gegenereerde klanken", silence: "Stilte", nature: "Natuur", binaural: "Binauraal", noise: "Ruis", volume: "Volume", headphones: "🎧 koptelefoon aangeraden",
        vagues: "Golven", vent: "Wind", pluie: "Regen", foret: "Bos", feu: "Kampvuur",
        delta: "Delta · slaap", theta: "Theta · meditatie", alpha: "Alfa · rust", beta: "Beta · focus", rose: "Roze ruis", brun: "Bruine ruis" },
};

const ICONS = { vagues: Waves, vent: Wind, pluie: CloudRain, foret: Trees, feu: Flame, delta: Brain, theta: Brain, alpha: Brain, beta: Brain, rose: AudioLines, brun: AudioLines };

const TABS = [
  { id: "nature", keys: NATURE_KEYS },
  { id: "binaural", keys: BINAURAL_KEYS },
  { id: "noise", keys: NOISE_KEYS },
];

export function AmbientPlayer() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const L = LABELS[lang] || LABELS.fr;
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("nature");
  const [mood, setMood] = useState(() => localStorage.getItem("coccon_ambient_mood") || null);
  const [playing, setPlaying] = useState(false);
  const [vol, setVol] = useState(() => getVolume());

  useEffect(() => () => stopAmbient(), []);

  const select = (key) => {
    startAmbient(key);
    setMood(key); setPlaying(true);
    localStorage.setItem("coccon_ambient_mood", key);
  };
  const silence = () => { stopAmbient(); setPlaying(false); };
  const onVol = (e) => { const v = parseFloat(e.target.value); setVol(v); setVolume(v); };

  const activeTab = TABS.find((t) => t.id === tab) || TABS[0];

  const MoodBtn = ({ k }) => {
    const Icon = ICONS[k];
    const active = playing && mood === k;
    return (
      <button
        onClick={() => select(k)}
        data-testid={`ambient-mood-${k}`}
        className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors ${active ? "bg-primary/15 text-primary border border-primary/40" : "text-foreground/70 hover:text-primary hover:bg-white/5 border border-transparent"}`}
      >
        <Icon size={15} className="flex-shrink-0" />
        <span className="flex-1 text-left">{L[k]}</span>
        {active && (
          <span className="flex items-end gap-0.5 h-3.5" aria-hidden>
            <span className="w-0.5 bg-primary rounded-full ambient-bar" style={{ animationDelay: "0s" }} />
            <span className="w-0.5 bg-primary rounded-full ambient-bar" style={{ animationDelay: "0.25s" }} />
            <span className="w-0.5 bg-primary rounded-full ambient-bar" style={{ animationDelay: "0.5s" }} />
          </span>
        )}
      </button>
    );
  };

  const VolIcon = vol < 0.02 ? VolumeX : vol < 0.5 ? Volume1 : Volume2;

  return (
    <div className="fixed bottom-24 right-4 md:bottom-4 z-[60] flex flex-col items-end gap-3 pointer-events-none" data-testid="ambient-player">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
            className="w-64 max-h-[75vh] overflow-y-auto rounded-2xl glass border border-primary/20 p-4 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.7)] pointer-events-auto"
            data-testid="ambient-panel"
          >
            <div className="flex items-center gap-2 mb-1">
              <Music2 size={15} className="text-primary" />
              <h4 className="font-display text-foreground/90 text-base">{L.title}</h4>
            </div>
            <p className="text-foreground/40 text-[11px] italic mb-3">{L.hint}</p>

            {/* Onglets */}
            <div className="flex items-center gap-1 p-1 rounded-xl bg-white/5 border border-white/10 mb-3">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`ambient-tab-${t.id}`}
                  className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${tab === t.id ? "bg-primary/20 text-primary" : "text-foreground/50 hover:text-foreground/80"}`}
                >
                  {L[t.id]}
                </button>
              ))}
            </div>

            {tab === "binaural" && (
              <p className="text-foreground/40 text-[11px] mb-2">{L.headphones}</p>
            )}

            <div className="space-y-1.5 mb-3">
              {activeTab.keys.map((k) => <MoodBtn key={k} k={k} />)}
            </div>

            {/* Curseur de volume */}
            <div className="flex items-center gap-2 px-1 mb-3" data-testid="ambient-volume-row">
              <VolIcon size={15} className="text-foreground/50 flex-shrink-0" />
              <input
                type="range" min="0" max="1" step="0.01" value={vol}
                onChange={onVol}
                data-testid="ambient-volume-slider"
                aria-label={L.volume}
                className="flex-1 h-1 accent-primary cursor-pointer"
              />
            </div>

            <button
              onClick={silence}
              data-testid="ambient-silence"
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors ${!playing ? "bg-white/5 text-foreground/80 border border-white/10" : "text-foreground/50 hover:text-foreground hover:bg-white/5 border border-transparent"}`}
            >
              <VolumeX size={15} className="flex-shrink-0" />
              <span className="flex-1 text-left">{L.silence}</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="ambient-toggle"
        aria-label={L.title}
        className={`pointer-events-auto relative w-12 h-12 rounded-full flex items-center justify-center border transition backdrop-blur-md ${playing ? "bg-primary/20 border-primary/50 text-primary opacity-100" : `bg-background/70 border-white/15 text-foreground/60 hover:text-primary hover:border-primary/40 ${open ? "opacity-100" : "opacity-50 hover:opacity-100"}`}`}
      >
        <Music2 size={19} />
        {playing && <span className="absolute inset-0 rounded-full ring-2 ring-primary/40 animate-ping" />}
      </button>
    </div>
  );
}
